import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, pairwise, switchMap, take } from 'rxjs/operators';
import { AuthActions } from './auth/actions';
import * as fromAuth from './auth/reducers';
import { CustomerFacade } from './facades/customer.facade';
import { SeoFacade } from './facades/seo.facade';
import { StoreConfigurationFacade } from './facades/store-configuration.facade';
import { StoreSelectionFacade } from './facades/store-selection.facade';
import { I18nService } from './services';
import { AppUtils } from './utils/app.utils';
import { LocalStorageUtils } from './utils/localStorage.utils';
import { UrlUtils } from './utils/url.utils';
import { environment } from '../environments/environment';
import { CmsFacade } from './facades/cms.facade';
import { CatalogActions, GuestCartActions } from './actions';
import { EFeatureToggles } from './configurations/common';
import { ConfigurationFacade } from './facades/configuration.facade';
import { combineLatest } from 'rxjs';
import { FooterFacade } from './facades/footer.facade';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  showFullPageLoader = false;
  fullPageLoaderList = [];
  isExternalQuoteCheckoutLoaded: boolean = false;

  constructor(
    private store: Store<fromAuth.State>,
    private router: Router,
    private route: ActivatedRoute,
    private seoFacade: SeoFacade,
    private customerFacade: CustomerFacade,
    private i18nService: I18nService,
    private storeSelectionFacade: StoreSelectionFacade,
    private storeConfigurationFacade: StoreConfigurationFacade,
    private cmsFacade: CmsFacade,
    private configurationFacade: ConfigurationFacade,
    private footerFacade: FooterFacade,
  ) {
    seoFacade.setSeoForPage();

    router.events.pipe(
      filter(event => event instanceof RoutesRecognized),
      pairwise(),
    ).subscribe((routes: any[]) => {
      if (UrlUtils.isTheSameRoute(routes)) {
        return;
      }
      LocalStorageUtils.setKeyValue('previousPage', routes[0].url);
    });
  }

  /**
   * Guest application mode is activated in case of user is on EU store, and then
   * we need to reduce unnecessary calls and silent login from TpF.
   */
  ngOnInit(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.EXTERNAL_QUOTE_CHECKOUT).pipe(
      take(1),
    ).subscribe(isExternalQuoteCheckoutEnabled => {

      //dispatching actions to fetch footer navigation and social media footer
      this.footerFacade.dispatchFetchFooterNavigation();
      this.footerFacade.dispatchFetchSocialMediaFooter();

      if (!isExternalQuoteCheckoutEnabled) {
        this.store.dispatch(AuthActions.handleAuth());
        this.store.dispatch(CatalogActions.loadHowManyProductShouldBeCached());
      }

      this._handleRouteParamsAndData(isExternalQuoteCheckoutEnabled);
      this.storeConfigurationFacade.dispatchFetchStoreConfiguration(
        AppUtils.getCurrentStore().storeId,
      );

      if (!isExternalQuoteCheckoutEnabled) {
        this.cmsFacade.getHomepageContentBannerData(environment.homePageBannerKey);
        this.cmsFacade.getStoreSelectionContentBannerData(environment.storeSelectionBannerKey);
      }
    });
  }

  /**
   * Handles reactive subscription to router events and updates component state based on route params and data.
   *
   * This method listens for `NavigationEnd` events and extracts the deepest child route to access
   * parameters, route data, and the current URL. Based on this information:
   *
   * - It sets the store locale using `customerFacade`.
   * - Optionally triggers guest cart loading for external quote checkout scenarios.
   * - Updates full-page loader UI flags from route data.
   *
   * @param isExternalQuoteCheckoutEnabled
   * @private
   */
  private _handleRouteParamsAndData(isExternalQuoteCheckoutEnabled: boolean): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this._getDeepestRoute(this.route)),
      switchMap(currentRoute =>
        combineLatest([currentRoute.paramMap, currentRoute.data, currentRoute.url]).pipe(
          map(([paramMap, data, url]) => {
            const params = Object.fromEntries(paramMap.keys.map(key => [key, paramMap.get(key)]));
            return { params, data, url: url.map(url => url.path).join('/') };
          })
        )
      )
    ).subscribe(({ params, data, url }) => {
      const defaultLocale = this.i18nService.getCurrentLocale();
      const store = params?.lang || defaultLocale;

      this.customerFacade.setStore(store);

      if (
        isExternalQuoteCheckoutEnabled &&
        this._isGuestCartRoute(url) &&
        params?.guestUserId &&
        params?.guestCartId &&
        !this.isExternalQuoteCheckoutLoaded
      ) {
        this.store.dispatch(GuestCartActions.loadGuestCart({
          guestUserId: params.guestUserId,
          guestCartId: params.guestCartId
        }));
        this.isExternalQuoteCheckoutLoaded = true;
      }

      this.showFullPageLoader = data?.['fullPageLoader'] ?? false;
      this.fullPageLoaderList = data?.['fullPageLoaderList'] ?? [];
    });
  }

  /**
   * Get the deepest route in the router tree.
   *
   * @param {ActivatedRoute} route
   * @returns {ActivatedRoute}
   * @private
   */
  private _getDeepestRoute(route: ActivatedRoute): ActivatedRoute {
    return route.firstChild ? this._getDeepestRoute(route.firstChild) : route;
  }

  /**
   * Check if user is on store selection page.
   *
   * @returns {boolean}
   */
  isOnStoreSelection(): boolean {
    return this.storeSelectionFacade.isOnStoreSelectionPage();
  }

  /**
   * Check if current route is a guest cart route.
   *
   * @private
   * @param {string} url
   * @returns {boolean}
   */
  private _isGuestCartRoute(url: string): boolean {
    return environment.guestCartsRoutes.some(route => url?.includes(route));
  }
}
